import { useState } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CalendarDays, MapPin, Maximize, Navigation, Send, ShieldCheck, Store } from "lucide-react";
import { toast } from "sonner";

import { AppLogo } from "@/components/AppLogo";
import { ImageCarousel } from "@/components/ImageCarousel";
import { StatusBadge } from "@/components/StatusBadge";
import { ShareDialog } from "@/components/ShareDialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fetchPropertyDetail, type PropertyDetail } from "@/lib/property-details";
import { partnerSlug } from "@/lib/partner-public";

import { sendPublicEnquiry } from "@/lib/public-enquiry.functions";
import { listingSeoDescription, listingSeoTitle, publicListingUrl } from "@/lib/share";
import {
  buildSeo,
  isValidOgImage,
  jsonLdScript,
  listingJsonLd,
  breadcrumbJsonLd,
  canonicalUrl,
} from "@/lib/seo";

export const Route = createFileRoute("/l/$id")({
  loader: async ({ params }) => {
    const detail = await fetchPropertyDetail(params.id).catch(() => null);
    if (!detail) throw notFound();
    return { detail };
  },
  head: ({ params, loaderData }) => {
    const url = publicListingUrl(params.id);
    if (!loaderData) {
      return {
        meta: [{ title: "Listing unavailable | Landoo" }, { name: "robots", content: "noindex" }],
      };
    }
    const d = loaderData.detail;
    const seo = buildSeo({
      title: listingSeoTitle(d.title, d.priceLabel, d.city),
      description: listingSeoDescription({
        title: d.title,
        priceLabel: d.priceLabel,
        propertyType: d.propertyType,
        city: d.city,
        address: d.address,
        description: d.description,
      }),
      url,
      image: d.images.find((i) => isValidOgImage(i)) ?? null,
      imageKind: d.kind === "parking" ? "parking" : "listing",
      ogType: "article",
      nofollow: !d.verified,
      noindex: d.demo,
    });
    return {
      meta: seo.meta,
      links: seo.links,
      scripts: [
        ...jsonLdScript(
          listingJsonLd({
            seo,
            kind: d.kind,
            propertyType: d.propertyType,
            priceLabel: d.priceLabel,
            areaSqft: d.areaSqft,
            address: d.address,
            city: d.city,
            latitude: d.latitude,
            longitude: d.longitude,
            postedAt: d.postedAt,
            partnerName: d.partnerName,
            images: d.images,
          }),
        ),
        ...jsonLdScript(
          breadcrumbJsonLd([
            { name: "Landoo", url: canonicalUrl("/") },
            { name: d.city ?? "Listings", url: canonicalUrl("/search") },
            { name: seo.title, url: seo.url },
          ]),
        ),
      ],
    };
  },

  notFoundComponent: () => <Unavailable />,
  errorComponent: () => <Unavailable message="Something went wrong loading this listing." />,
  component: PublicListingPage,
});

function PublicListingPage() {
  const { detail } = Route.useLoaderData();
  return (
    <div className="flex min-h-screen flex-col bg-cream/30">
      <PublicHeader />
      <main className="mx-auto w-full max-w-5xl flex-1 space-y-4 p-4 md:p-6">
        <PublicListingView detail={detail} />
      </main>
      <PublicFooter />
    </div>
  );
}

function PublicHeader() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-4 py-3">
        <Link to="/" className="flex items-center gap-2">
          <AppLogo />
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
          >
            Explore map
          </Link>
          <Link
            to="/login"
            className="rounded-full bg-brand px-3 py-1.5 text-xs font-bold text-brown-dark transition hover:brightness-95"
          >
            Sign in
          </Link>
        </div>
      </div>
    </header>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-border bg-card px-4 py-6 text-center text-xs text-muted-foreground">
      Listing published on Landoo. Details are provided by the listing partner.
    </footer>
  );
}

function PublicListingView({ detail }: { detail: PropertyDetail }) {
  const [shareOpen, setShareOpen] = useState(false);

  const city = detail.city?.trim();
  const fullAddress = !city
    ? detail.address || "—"
    : detail.address?.toLowerCase().includes(city.toLowerCase())
      ? detail.address
      : [detail.address, city].filter(Boolean).join(", ");

  const posted = detail.postedAt
    ? new Date(detail.postedAt).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "Recently posted";

  const mapSrc =
    detail.latitude != null && detail.longitude != null
      ? `https://www.google.com/maps?q=${detail.latitude},${detail.longitude}&z=15&output=embed`
      : `https://www.google.com/maps?q=${encodeURIComponent(fullAddress)}&z=13&output=embed`;

  const directionsHref =
    detail.latitude != null && detail.longitude != null
      ? `https://www.google.com/maps/dir/?api=1&destination=${detail.latitude},${detail.longitude}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;

  return (
    <article className="space-y-4">
      <ImageCarousel images={detail.images} alt={detail.title} />

      <section className="rounded-3xl border border-border bg-card p-4 shadow-soft sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge purpose={detail.kind} />
          <span className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-brown-dark">
            {detail.propertyType}
          </span>
          {detail.verified && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rent/15 px-2.5 py-1 text-[11px] font-semibold text-rent">
              <ShieldCheck className="h-3.5 w-3.5" /> Verified
            </span>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-bold leading-tight text-brown-dark sm:text-2xl">
              {detail.title}
            </h1>
            <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 shrink-0" />
              <span className="truncate">{fullAddress}</span>
            </p>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-coral sm:text-2xl">{detail.priceLabel}</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {detail.kind === "parking" ? "Parking rate" : "Asking price"}
            </div>
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Fact
            icon={<Maximize className="h-4 w-4" />}
            label="Area"
            value={detail.areaSqft ? `${detail.areaSqft} sqft` : "—"}
          />
          <Fact icon={<CalendarDays className="h-4 w-4" />} label="Posted" value={posted} />
          <Fact
            icon={<Store className="h-4 w-4" />}
            label="Partner"
            value={detail.partnerName ?? "Landoo Partner"}
          />
        </dl>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="rounded-xl border border-border py-2.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary sm:text-sm"
          >
            Share / Copy link
          </button>
          <a
            href="#public-enquiry"
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-brand py-2.5 text-xs font-bold text-brown-dark shadow-soft transition hover:brightness-95 sm:text-sm"
          >
            <Send className="h-4 w-4" /> Send enquiry
          </a>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-4">
          <Panel title="Description">
            <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
              {detail.description}
            </p>
          </Panel>

          <Panel title="Amenities">
            {detail.amenities.length ? (
              <div className="flex flex-wrap gap-1.5">
                {detail.amenities.map((a) => (
                  <span
                    key={a}
                    className="rounded-full bg-brand-soft px-3 py-1 text-xs font-medium text-brown-dark"
                  >
                    {a}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No amenities listed.</p>
            )}
          </Panel>

          <Panel title="Location">
            <div className="overflow-hidden rounded-2xl border border-border">
              <iframe
                title={`Map location of ${detail.title}`}
                src={mapSrc}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="h-56 w-full sm:h-72"
              />
            </div>
            <a
              href={directionsHref}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
            >
              <Navigation className="h-3.5 w-3.5" /> Get directions
            </a>
          </Panel>
        </div>

        <div className="space-y-4">
          <div id="public-enquiry">
            <Panel title="Send enquiry">
              <PublicEnquiryForm detail={detail} />
            </Panel>
          </div>

          <Panel title="Listed by">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-soft text-brown-dark">
                <Store className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-brown-dark">
                  {detail.partnerName ?? "Landoo Partner"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {detail.verified ? "Verified partner" : "Verification in progress"}
                </div>
              </div>
            </div>
            {detail.ownerId && (
              <Link
                to="/p/$slug"
                params={{ slug: partnerSlug(detail.partnerName, detail.ownerId) }}
                className="mt-3 inline-flex rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
              >
                View partner profile
              </Link>
            )}

            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
              Contact details, favourites and reviews are available after you{" "}
              <Link to="/login" className="font-semibold text-coral underline">
                sign in
              </Link>
              .
            </p>
          </Panel>
        </div>
      </div>

      <ShareDialog
        listing={{ id: detail.id, title: detail.title, priceLabel: detail.priceLabel }}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </article>
  );
}

function PublicEnquiryForm({ detail }: { detail: PropertyDetail }) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [message, setMessage] = useState(
    `Hi, I'm interested in "${detail.title}". Please share more details.`,
  );
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!detail.enquiryEnabled || detail.demo || !detail.ownerId) {
    return (
      <p className="text-sm text-muted-foreground">
        Enquiries are not available for this listing right now.
      </p>
    );
  }

  if (sent) {
    return (
      <p className="text-sm font-medium text-rent">
        Enquiry sent. The partner will contact you on the number you shared.
      </p>
    );
  }

  const submit = async () => {
    if (name.trim().length < 2) return setError("Enter your name.");
    if (!/^[0-9+\-\s()]{7,20}$/.test(mobile.trim()))
      return setError("Enter a valid mobile number.");
    if (message.trim().length < 5) return setError("Add a short message.");
    setError(null);
    setBusy(true);
    try {
      await sendPublicEnquiry({
        data: {
          listingId: detail.id,
          service: detail.kind as "sale" | "rent" | "lease" | "parking",
          name: name.trim(),
          mobile: mobile.trim(),
          message: message.trim(),
        },
      });
      setSent(true);
      toast.success("Enquiry sent.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send the enquiry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label
        className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
        htmlFor="pub-name"
      >
        Name
      </label>
      <Input
        id="pub-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={80}
        className="mt-1"
      />

      <label
        className="mt-3 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
        htmlFor="pub-mobile"
      >
        Mobile
      </label>
      <Input
        id="pub-mobile"
        inputMode="tel"
        value={mobile}
        onChange={(e) => setMobile(e.target.value)}
        maxLength={20}
        placeholder="+91 98765 43210"
        className="mt-1"
      />

      <label
        className="mt-3 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
        htmlFor="pub-message"
      >
        Message
      </label>
      <Textarea
        id="pub-message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        maxLength={1000}
        className="mt-1"
      />

      {error && <p className="mt-2 text-[11px] text-destructive">{error}</p>}

      <button
        type="button"
        onClick={submit}
        disabled={busy}
        className="mt-4 w-full rounded-xl bg-brand py-2.5 text-sm font-bold text-brown-dark transition hover:brightness-95 disabled:opacity-60"
      >
        {busy ? "Sending…" : "Send enquiry"}
      </button>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-3xl border border-border bg-card p-4 shadow-soft sm:p-5">
      <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Fact({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-secondary/60 p-2.5">
      <dt className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground">
        {icon} {label}
      </dt>
      <dd className="mt-0.5 truncate text-sm font-semibold text-brown-dark">{value}</dd>
    </div>
  );
}

function Unavailable({ message }: { message?: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-cream/30 p-6 text-center">
      <h1 className="text-lg font-bold text-brown-dark">Listing unavailable</h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        {message ?? "This listing may have been removed or is no longer published."}
      </p>
      <Link to="/" className="rounded-full bg-brand px-4 py-2 text-sm font-bold text-brown-dark">
        Explore the map
      </Link>
    </div>
  );
}
